import type {
	ShadowLocation,
	ShadowSensor,
	ShadowZone,
} from "../../types/location-map";
import type { SensorLngLatById } from "../../utils/location/location-map-utils";

export const cloneLocation = (location: ShadowLocation): ShadowLocation =>
	structuredClone(location);

export const updateSensorInLocation = (
	location: ShadowLocation,
	sensorId: string,
	updater: (sensor: ShadowSensor, zone: ShadowZone) => ShadowSensor,
	nextZoneId?: string,
): ShadowLocation => {
	const zones = location.zones ?? [];
	let moved: ShadowSensor | undefined;
	let fromZoneId: string | undefined;

	const withoutSensor = zones.map((zone) => {
		const sensors = zone.sensors ?? [];
		const remaining: ShadowSensor[] = [];
		for (const sensor of sensors) {
			if (sensor.id === sensorId) {
				moved = updater(sensor, zone);
				fromZoneId = zone.id;
				continue;
			}
			remaining.push(sensor);
		}
		if (remaining.length === sensors.length) return zone;
		return {
			...zone,
			sensorIds: remaining.map((sensor) => sensor.id),
			sensors: remaining,
		};
	});

	if (!moved) return location;

	const targetZoneId = nextZoneId ?? fromZoneId;
	if (!targetZoneId) return location;

	return {
		...location,
		zones: withoutSensor.map((zone) => {
			if (zone.id !== targetZoneId) return zone;
			const nextSensor = { ...moved!, zone: zone.id };
			const sensors = [...(zone.sensors ?? []), nextSensor];
			return {
				...zone,
				sensorIds: sensors.map((sensor) => sensor.id),
				sensors,
			};
		}),
	};
};

export const updateZoneInLocation = (
	location: ShadowLocation,
	zoneId: string,
	updater: (zone: ShadowZone) => ShadowZone,
): ShadowLocation => ({
	...location,
	zones: (location.zones ?? []).map((zone) =>
		zone.id === zoneId ? updater(zone) : zone,
	),
});

/** Apply a zone patch, treating `floor: null` as clearing the assignment. */
export const mergeZonePatch = (
	zone: ShadowZone,
	zoneData: Partial<Omit<ShadowZone, "floor">> & {
		floor?: null | ShadowZone["floor"];
	},
): ShadowZone => {
	const { floor, ...rest } = zoneData;
	const next: ShadowZone = { ...zone, ...rest };
	if (floor === null) {
		delete next.floor;
		return next;
	}
	if (floor !== undefined) {
		next.floor = floor;
	}
	return next;
};

export const applySensorCoordinatesToLocation = (
	location: ShadowLocation,
	target: SensorLngLatById,
): ShadowLocation => {
	let next = location;
	for (const [sensorId, coordinates] of Object.entries(target)) {
		next = updateSensorInLocation(next, sensorId, (sensor) => ({
			...sensor,
			geolocation: { coordinates, type: "Point" },
		}));
	}
	return next;
};
